import React, { useRef, useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, RotateCcw, GripVertical, Plus } from 'lucide-react';
import { Button } from './Button';
import { scaleImage, isValidImageType } from '../../utils/imageUtils';

interface ImageSlot {
  id: string;
  type: 'front' | 'back' | 'detail' | 'image';  // 'image' for generic slots
  label: string;
  url: string;
  file?: File;
  order?: number; // Position in upload order (0 = primary image)
  // Temporary file fields (from Google Drive import)
  temp_path?: string;
  drive_file_id?: string;
  original_name?: string;
  filename?: string;
}

interface MultiImageUploadProps {
  images: ImageSlot[];
  onImagesChange: (images: ImageSlot[]) => void;
  disabled?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxImages?: number;  // Maximum number of image slots allowed (defaults to 10)
  minImages?: number;  // Minimum number of image slots to show initially (defaults to 1)
}

export const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
  images,
  onImagesChange,
  disabled = false,
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.9,
  maxImages = 10,
  minImages = 1,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((index: number) => (e: React.DragEvent) => {
    if (disabled || processing || !images[index].url) return;
    setDragSourceIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, [disabled, processing, images]);

  const handleDragOver = useCallback((index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragSourceIndex !== null && dragSourceIndex !== index) {
      setDragOverIndex(index);
    }
  }, [dragSourceIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((targetIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragSourceIndex === null || dragSourceIndex === targetIndex) {
      setDragSourceIndex(null);
      setDragOverIndex(null);
      return;
    }
    // Swap image content between slots, preserving slot id/label/type
    const newImages = images.map((img, i) => {
      if (i === targetIndex) {
        return { ...img, url: images[dragSourceIndex].url, file: images[dragSourceIndex].file, temp_path: images[dragSourceIndex].temp_path, drive_file_id: images[dragSourceIndex].drive_file_id, original_name: images[dragSourceIndex].original_name, filename: images[dragSourceIndex].filename };
      }
      if (i === dragSourceIndex) {
        return { ...img, url: images[targetIndex].url, file: images[targetIndex].file, temp_path: images[targetIndex].temp_path, drive_file_id: images[targetIndex].drive_file_id, original_name: images[targetIndex].original_name, filename: images[targetIndex].filename };
      }
      return img;
    });
    onImagesChange(newImages);
    setDragSourceIndex(null);
    setDragOverIndex(null);
  }, [dragSourceIndex, images, onImagesChange]);

  const handleDragEnd = useCallback(() => {
    setDragSourceIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setError(null);

    // Validate all files
    const invalidFiles = files.filter(file => !isValidImageType(file));
    if (invalidFiles.length > 0) {
      setError(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}. Please select valid image files (JPEG, PNG, GIF, or WebP)`);
      return;
    }

    // Check file sizes (10MB limit each)
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const oversizedFiles = files.filter(file => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      setError(`Files too large: ${oversizedFiles.map(f => f.name).join(', ')}. Each file must be less than 10MB`);
      return;
    }

    // Limit total slots to maxImages
    const availableSlots = maxImages - images.filter(img => img.url).length;
    const filesToProcess = files.slice(0, availableSlots);
    if (files.length > availableSlots) {
      setError(`Only ${availableSlots} more image${availableSlots !== 1 ? 's' : ''} can be added (max ${maxImages}). Ignored: ${files.slice(availableSlots).map(f => f.name).join(', ')}`);
    }

    setProcessing(true);

    try {
      // Scale all images
      const scaledFiles = await Promise.all(
        filesToProcess.map(async (file) => {
          const scaledResult = await scaleImage(file, maxWidth, maxHeight, quality);
          return scaledResult.file;
        })
      );

      // If we have multiple files, assign in upload order (AI handles classification later)
      if (scaledFiles.length > 1) {
        const newImages = [...images];
        // Fill empty slots first, then expand if needed
        let slotIndex = 0;
        for (const file of scaledFiles) {
          // Find next empty slot
          while (slotIndex < newImages.length && newImages[slotIndex].url) {
            slotIndex++;
          }
          if (slotIndex < newImages.length) {
            // Fill existing empty slot
            newImages[slotIndex] = {
              ...newImages[slotIndex],
              url: URL.createObjectURL(file),
              file,
            };
          } else if (newImages.length < maxImages) {
            // Add a new slot
            newImages.push({
              id: String(newImages.length + 1),
              type: 'image',
              label: `Image ${newImages.length + 1}`,
              url: URL.createObjectURL(file),
              file,
              order: newImages.length,
            });
          }
          slotIndex++;
        }
        onImagesChange(newImages);
      } else {
        // Single file - assign to first empty slot, or add a new slot if all full
        const newImages = [...images];
        const emptySlotIndex = newImages.findIndex(img => !img.url);

        if (emptySlotIndex >= 0) {
          newImages[emptySlotIndex] = {
            ...newImages[emptySlotIndex],
            url: URL.createObjectURL(scaledFiles[0]),
            file: scaledFiles[0],
          };
        } else if (newImages.length < maxImages) {
          // All slots full — add a new slot
          newImages.push({
            id: String(newImages.length + 1),
            type: 'image',
            label: `Image ${newImages.length + 1}`,
            url: URL.createObjectURL(scaledFiles[0]),
            file: scaledFiles[0],
            order: newImages.length,
          });
        } else {
          setError(`Maximum of ${maxImages} images reached.`);
        }
        onImagesChange(newImages);
      }
    } catch (error) {
      console.error('Error processing images:', error);
      setError(error instanceof Error ? error.message : 'Failed to process images');
    } finally {
      setProcessing(false);
    }
  };


  const handleClick = () => {
    if (!disabled && !processing) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveImage = (index: number) => {
    if (!disabled && !processing) {
      const newImages = [...images];
      if (newImages[index].url) {
        URL.revokeObjectURL(newImages[index].url);
      }
      newImages[index].url = '';
      newImages[index].file = undefined;
      onImagesChange(newImages);
    }
  };

  const handleReplaceImage = async (index: number, file: File) => {
    if (disabled || processing) return;

    // Validate file
    if (!isValidImageType(file)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    const maxFileSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxFileSize) {
      setError(`File size must be less than ${maxFileSize / (1024 * 1024)}MB`);
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Scale the image
      const scaledFile = await scaleImage(file, maxWidth, maxHeight, quality);
      const url = URL.createObjectURL(scaledFile);

      // Replace in images array, preserving id and type
      const newImages = [...images];

      // Revoke old URL to free memory
      if (newImages[index].url) {
        URL.revokeObjectURL(newImages[index].url);
      }

      newImages[index] = {
        ...newImages[index],
        file: scaledFile,
        url: url,
        // Preserve existing id and type
      };

      onImagesChange(newImages);
      console.log(`Image ${index} (${newImages[index].type}) replaced successfully`);
    } catch (error) {
      console.error('Error replacing image:', error);
      setError('Failed to process the replacement image. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleClearAll = () => {
    if (!disabled && !processing) {
      const newImages = [...images];
      newImages.forEach(img => {
        if (img.url) {
          URL.revokeObjectURL(img.url);
          img.url = '';
          img.file = undefined;
        }
      });
      onImagesChange(newImages);
      setError(null);
    }
  };

  const handleAddSlot = () => {
    if (disabled || processing || images.length >= maxImages) return;
    const newSlot: ImageSlot = {
      id: String(images.length + 1),
      type: 'image',
      label: `Image ${images.length + 1}`,
      url: '',
      order: images.length,
    };
    onImagesChange([...images, newSlot]);
  };

  const handleRemoveSlot = (index: number) => {
    if (disabled || processing || images.length <= minImages) return;
    const newImages = images
      .filter((_, i) => i !== index)
      .map((img, i) => ({
        ...img,
        id: String(i + 1),
        label: `Image ${i + 1}`,
        order: i,
      }));
    onImagesChange(newImages);
  };

  const hasAnyImages = images.some(img => img.url);
  const canAddMore = images.length < maxImages;

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onClick={handleClick}
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
          disabled || processing
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : hasAnyImages
            ? 'border-green-300 bg-green-50 cursor-pointer hover:bg-green-100'
            : 'border-gray-300 bg-gray-50 cursor-pointer hover:bg-gray-100'
        }`}
      >
        {processing ? (
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-blue-500 mb-2">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
            <p className="text-sm text-blue-600 font-medium">Processing images...</p>
          </div>
        ) : (
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
              {disabled ? <ImageIcon className="w-full h-full" /> : <Upload className="w-full h-full" />}
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium mb-2">
                {disabled ? 'Image upload disabled' : hasAnyImages ? 'Click to replace images' : 'Click to upload images'}
              </p>
              <p className="text-xs text-gray-500 mb-2">
                Upload images (PNG, JPG, GIF, WebP up to 10MB each). Max {maxImages}.
              </p>
              <p className="text-xs text-gray-400">
                Drag images to reorder. AI will auto-classify after analysis.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {hasAnyImages && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            disabled={disabled || processing}
            icon={RotateCcw}
            className="text-gray-500 hover:text-gray-700"
          >
            Clear All
          </Button>
        </div>
      )}

      {/* Image Preview Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {images.map((image, index) => (
          <div
            key={image.id}
            className="space-y-2"
            draggable={!disabled && !processing && !!image.url}
            onDragStart={handleDragStart(index)}
            onDragOver={handleDragOver(index)}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop(index)}
            onDragEnd={handleDragEnd}
          >
            <label className="block text-sm font-medium text-gray-700">{image.label}</label>
            <div className={`relative aspect-square border-2 border-dashed rounded-lg overflow-hidden group transition-all ${
              dragOverIndex === index
                ? 'border-blue-500 bg-blue-50 scale-105'
                : dragSourceIndex === index
                ? 'border-gray-400 opacity-50'
                : 'border-gray-300'
            }`}>
              {image.url ? (
                <>
                  <img
                    src={image.url}
                    alt={image.label}
                    className="w-full h-full object-cover"
                  />
                  {!disabled && !processing && (
                    <>
                      {/* Drag Handle */}
                      <div className="absolute top-2 left-2 bg-white/80 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10" title="Drag to reorder">
                        <GripVertical className="w-4 h-4 text-gray-600" />
                      </div>

                      {/* Replace Button (shows on hover) */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center pointer-events-none">
                        <label className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleReplaceImage(index, file);
                              }
                              e.target.value = '';
                            }}
                          />
                          <button
                            type="button"
                            className="px-3 py-1 bg-white text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors shadow-lg"
                          >
                            Replace
                          </button>
                        </label>
                      </div>

                      {/* Remove Button — removes slot if above min, otherwise just clears image */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (images.length > minImages) {
                            handleRemoveSlot(index);
                          } else {
                            handleRemoveImage(index);
                          }
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-lg z-10"
                        title={images.length > minImages ? 'Remove image slot' : 'Clear image'}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className={`w-full h-full flex items-center justify-center ${
                  dragOverIndex === index ? 'bg-blue-50' : 'bg-gray-50'
                }`}>
                  <ImageIcon className={`w-8 h-8 ${dragOverIndex === index ? 'text-blue-400' : 'text-gray-400'}`} />
                </div>
              )}
            </div>
          </div>
        ))}
        {/* Add Image Slot Button */}
        {canAddMore && !disabled && !processing && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 invisible">Add</label>
            <button
              type="button"
              onClick={handleAddSlot}
              className="w-full aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              <Plus className="w-8 h-8 text-gray-400" />
              <span className="text-xs text-gray-500 mt-1">Add Image</span>
            </button>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || processing}
      />

    </div>
  );
};